// PatchableAppsTable.tsx
import { useEffect, useState, useCallback, useMemo } from "react";
import {
    Search,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    PackageOpen,
    ChevronUp,
    ChevronDown,
    Loader2,
    Wrench,
    Plus,
    ListChecks,
} from "lucide-react";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";
import { showError } from "../../utils/toast";
import baseapi from "../../services/baseapi";
import { useClientLevel } from "../../hooks/useClientLevel";
import PatchableAssetsPanel from "./PatchableAssetsPanel"; // <-- Import new component
import { useNavigate } from "react-router-dom";
import AddPatchForm from "./AddPatchForm";
import { hasPermission } from "../../utils/permissionsHelper";
import ColumnVisibilityDropdown, { type ColumnOption } from "../../components/ColumnVisibilityDropdown";


interface PatchableApp {
    package_id: number;
    package_identifier: string;
    package_name: string;
    publisher: string;
    latest_version: string;
    patchable_asset_count: number;
    installed_version: string;
}


interface ApiResponse {
    status: boolean;
    message: string;
    data: PatchableApp[];
    total?: number; // Expected from the API for pagination
}


type SortKey =
    | "package_name"
    | "publisher"
    | "latest_version"
    | "installed_version"
    | "patchable_asset_count";

export type PatchableAppColumnKey =
    | "package_name"
    | "package_identifier"
    | "publisher"
    | "installed_version"
    | "latest_version"
    | "patchable_asset_count"
    | "actions";

export default function PatchableAppsTable() {
    const [apps, setApps] = useState<PatchableApp[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showAddForm, setShowAddForm] = useState(false);
    const canCreate = hasPermission('agent_patch_jobs', 'create');

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Record<PatchableAppColumnKey, boolean>>({
        package_name: true,
        package_identifier: true,
        publisher: true,
        installed_version: true,
        latest_version: true,
        patchable_asset_count: true,
        actions: true,
    });

    const columnOptions: ColumnOption<PatchableAppColumnKey>[] = useMemo(() => [
        { key: "package_name", label: "Application" },
        { key: "package_identifier", label: "Identifier" },
        { key: "publisher", label: "Publisher" },
        { key: "installed_version", label: "Installed" },
        { key: "latest_version", label: "Latest" },
        { key: "patchable_asset_count", label: "Assets" },
        { key: "actions", label: "Actions" },
    ], []);

    const visibleColsCount = Object.values(visibleColumns).filter(Boolean).length || 1;

    // Panel State
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
    const [selectedPackageName, setSelectedPackageName] = useState<string>("");


    const level = useClientLevel();
    const client_id = level?.id;


    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);


    const [sortKey, setSortKey] = useState<SortKey>("patchable_asset_count");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
    const navigate = useNavigate();


    // Debounce Search
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [searchTerm]);


    // API Call
    const fetchApps = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (debouncedSearch) {
                queryObj.$or = [
                    { package_name: { $ilike: `%${debouncedSearch}%` } },
                    { package_identifier: { $ilike: `%${debouncedSearch}%` } },
                    { publisher: { $ilike: `%${debouncedSearch}%` } }
                ];
            }


            const params: any = {
                client_id,
                skip: (currentPage - 1) * limit,
                limit: limit,
                search: debouncedSearch || undefined,
                sort_by: sortKey,
                sort_dir: sortDir,
                query: JSON.stringify(queryObj)
            };


            const res = await baseapi.get<ApiResponse>("/patch/patchable_apps", { params });


            if (res.data.status) {
                setApps(res.data.data ?? []);
                setTotalRecords(res.data.total ?? res.data.data?.length ?? 0);
            } else {
                showError(res.data.message || "Failed to load patchable applications");
            }
        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    }, [client_id, debouncedSearch, currentPage, limit, sortKey, sortDir]);


    useEffect(() => {
        if (client_id) {
            fetchApps();
        } else {
            navigate("/dashboard");
        }
    }, [fetchApps]);


    // Pagination Calculations
    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);


    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
        setCurrentPage(1);
    };

    const handleOpenPanel = (package_id: number, package_name: string) => {
        setSelectedPackageId(package_id);
        setSelectedPackageName(package_name);
        setIsPanelOpen(true);
    };


    const SortHeader = ({ label, keyName }: { label: string; keyName: SortKey }) => {
        const active = sortKey === keyName;
        const Icon = sortDir === "asc" ? ChevronUp : ChevronDown;
        return (
            <th
                onClick={() => handleSort(keyName)}
                className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer select-none group"
            >
                <div className="flex items-center gap-1 group-hover:text-orange-300 transition-colors">
                    {label}
                    {active ? (
                        <Icon className="w-3 h-3 text-orange-400" />
                    ) : (
                        <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-40" />
                    )}
                </div>
            </th>
        );
    };



    return (
        <div className="flex p-1 flex-col bg-transparent text-gray-200 relative overflow-hidden">

            {/* Sliding Panel */}
            <PatchableAssetsPanel
                isOpen={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                clientId={client_id}
                packageId={selectedPackageId!}
                packageName={selectedPackageName}
                search={debouncedSearch}
            />

            <AddPatchForm
                isOpen={showAddForm}
                onClose={() => {
                    setShowAddForm(false);

                }}
            />

            {/* Header / Toolbar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-1 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="bg-orange-600 p-1.5 rounded border border-orange-500/30">
                        <PackageOpen size={16} className="text-white" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <h1 className="text-lg font-bold text-white tracking-wide">
                            Patchable Applications
                        </h1>
                        {totalRecords > 0 && (
                            <span className="text-white font-bold">
                                ({totalRecords})
                            </span>
                        )}
                    </div>
                </div>



                <div className="flex items-center gap-2 ml-auto">

                    <button
                        onClick={() => navigate(`/patching/jobs?prev=application&tab=application`)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 rounded-lg text-gray-300 hover:text-white text-[11px] font-bold uppercase tracking-widest transition-all shadow-sm group"
                    >
                        <ListChecks size={14} className="text-gray-400 group-hover:text-orange-400 transition-colors" />
                        Jobs
                    </button>

                    <button
                        onClick={fetchApps}
                        disabled={loading}
                        className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw
                            size={14}
                            className={loading ? "animate-spin text-orange-400" : ""}
                        />
                    </button>
                    <div className="relative w-60 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 group-focus-within:text-orange-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name, publisher..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-2 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 transition-all"
                        />
                    </div>
                    <ColumnVisibilityDropdown
                        columns={columnOptions}
                        visibleColumns={visibleColumns}
                        onChange={setVisibleColumns}
                    />
                     {canCreate && (
                        <button
                            onClick={() => setShowAddForm(true)}
                            className="flex items-center gap-1.5 bg-orange-700 rounded-lg px-4 py-2 text-[11px] text-white font-bold hover:bg-orange-600 transition-all"
                        >
                            <Plus className="w-4 h-4" />
                            Force Patch
                        </button>
                    )}
                </div>
            </div>



            {/* Table container */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                {visibleColumns.package_name && (
                                    <SortHeader label="Application" keyName="package_name" />
                                )}
                                {visibleColumns.package_identifier && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                        Identifier
                                    </th>
                                )}
                                {visibleColumns.publisher && (
                                    <SortHeader label="Publisher" keyName="publisher" />
                                )}
                                {visibleColumns.installed_version && (
                                    <SortHeader label="Installed" keyName="installed_version" />
                                )}
                                {visibleColumns.latest_version && (
                                    <SortHeader label="Latest" keyName="latest_version" />
                                )}
                                {visibleColumns.patchable_asset_count && (
                                    <SortHeader label="Assets" keyName="patchable_asset_count" />
                                )}
                                {visibleColumns.actions && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-orange-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">
                                                Loading patchable applications...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : apps.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <PackageOpen size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                No patchable applications found
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                apps.map((app) => (
                                    <tr key={app.package_id} className="hover:bg-gray-700/50 transition-colors">
                                        {/* Application */}
                                        {visibleColumns.package_name && (
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] font-bold text-gray-100">
                                                        {app.package_name || app.package_identifier}
                                                    </span>
                                                </div>
                                            </td>
                                        )}

                                        {/* Identifier */}
                                        {visibleColumns.package_identifier && (
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <span className="font-mono text-[10px] text-gray-200">
                                                    {app.package_identifier}
                                                </span>
                                            </td>
                                        )}

                                        {/* Publisher */}
                                        {visibleColumns.publisher && (
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <span className="text-[10px] text-gray-200">
                                                    {app.publisher || "—"}
                                                </span>
                                            </td>
                                        )}

                                        {/* Installed */}
                                        {visibleColumns.installed_version && (
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <span className="font-mono text-[10px] text-orange-400">
                                                    {app.installed_version || "—"}
                                                </span>
                                            </td>
                                        )}

                                        {/* Latest */}
                                        {visibleColumns.latest_version && (
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <span className="font-mono text-[10px] text-emerald-400">
                                                    {app.latest_version || "—"}
                                                </span>
                                            </td>
                                        )}

                                        {/* Patchable assets */}
                                        {visibleColumns.patchable_asset_count && (
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                                                    {app.patchable_asset_count}
                                                </span>
                                            </td>
                                        )}

                                        {/* Actions */}
                                        {visibleColumns.actions && (
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <button
                                                    onClick={() => handleOpenPanel(app.package_id, app.package_name || app.package_identifier)}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 cursor-pointer text-[9px] font-bold uppercase tracking-widest bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:border-orange-500/50 rounded transition-colors"
                                                >
                                                    <Wrench size={10} />
                                                    Patch
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>



                {/* Pagination footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => {
                                setLimit(v);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-600">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="text-[9px] text-orange-400 hover:text-orange-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            disabled={currentPage === 1 || loading}
                            onClick={() => setCurrentPage(1)}
                            className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                        >
                            <ChevronsLeft size={14} />
                        </button>
                        <button
                            disabled={currentPage === 1 || loading}
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                            {currentPage}
                        </div>
                        <button
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                        >
                            <ChevronRight size={14} />
                        </button>
                        <button
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => setCurrentPage(totalPages)}
                            className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                        >
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}